import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../api';
import { errMessage, primaryBtnStyle } from '../UI/ink';
import { OUTCOMES, caseRef } from './model';

// Closing needs an outcome and a summary. It offers to resolve the case's
// open findings, and says plainly that a paused playbook run is not stopped.
export default function CloseCaseDialog({ kase, onClose, onClosed }) {
  const openFindings = (kase.findings || []).filter(f => f.status && f.status !== 'resolved');
  const waiting = (kase.runs || []).filter(r => r.status === 'waiting_approval' || r.status === 'running');
  const [outcome, setOutcome] = useState(null);
  const [summary, setSummary] = useState('');
  const [resolve, setResolve] = useState(openFindings.length > 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const firstRef = useRef(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ok = outcome && summary.trim();
  const submit = async () => {
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await apiClient.post(`/api/cases/${kase.id}/close`, { outcome, resolution_summary: summary.trim(), resolve_findings: resolve });
      onClosed();
    } catch (err) {
      setError(errMessage(err, 'Could not close the case.'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh] overflow-y-auto" style={{ background: 'rgba(0,0,0,0.6)' }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="close-title" className="w-[580px] max-w-full box-border flex flex-col gap-4 px-6 py-[22px] mb-10 rounded-[14px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-link)' }}>{caseRef(kase.id)}</span>
          <h2 id="close-title" className="m-0 font-heading text-xl font-semibold">Close this case</h2>
        </div>
        <fieldset className="m-0 p-0 border-0 flex flex-col gap-2">
          <legend className="pb-2 text-[13px] font-medium">Outcome</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {OUTCOMES.map(([v, t, d], i) => (
              <label key={v} className="flex items-start gap-2.5 px-3 py-2.5 rounded-[9px] border cursor-pointer" style={{ borderColor: outcome === v ? 'var(--accent)' : 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                <input ref={i === 0 ? firstRef : undefined} type="radio" name="outcome" checked={outcome === v} onChange={() => setOutcome(v)} className="mt-0.5 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
                <span className="flex flex-col gap-0.5"><span className="text-[13px] font-medium">{t}</span><span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{d}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="flex flex-col gap-1.5 text-[13px] font-medium">Resolution summary
          <textarea rows={4} value={summary} onChange={e => setSummary(e.target.value)} placeholder="What happened, what you did, and anything left to watch." className="box-border px-3 py-2.5 rounded-[10px] border text-[13px] font-normal leading-normal resize-y outline-none focus:border-[color:var(--accent)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
        </label>
        {(openFindings.length > 0 || waiting.length > 0) && (
          <div className="flex flex-col gap-2 px-3.5 py-3 rounded-[10px]" style={{ background: 'var(--surf-raised)' }}>
            {openFindings.length > 0 && (
              <label className="flex items-start gap-2.5 text-[13px] leading-normal cursor-pointer">
                <input type="checkbox" checked={resolve} onChange={e => setResolve(e.target.checked)} className="mt-[3px] w-4 h-4" style={{ accentColor: 'var(--accent)' }} />
                <span>Also resolve the {openFindings.length} open finding{openFindings.length === 1 ? '' : 's'} in this case
                  <span className="block font-mono text-xs" style={{ color: 'var(--ink-faint)' }}>{openFindings.slice(0, 4).map(f => f.finding_id).join(', ')}{openFindings.length > 4 ? ` +${openFindings.length - 4}` : ''}</span>
                </span>
              </label>
            )}
            {waiting.map(r => (
              <span key={r.id} className="flex gap-2.5 text-[13px] leading-normal">
                <span className="w-[7px] h-[7px] mt-1.5 shrink-0 rounded-sm" style={{ background: 'var(--sev-high-fg)' }} />
                <span>RUN-{r.id.slice(0, 8).toUpperCase()} is {r.status === 'running' ? 'still running' : 'waiting for approval'}. Closing the case doesn’t stop it; <Link to={`/playbooks?run=${r.id}`} style={{ color: 'var(--ink-link)' }}>answer it in Playbooks</Link>.</span>
              </span>
            ))}
          </div>
        )}
        {error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={!ok || busy} onClick={submit} className="h-11 px-[18px] rounded-[10px] text-sm font-semibold disabled:opacity-45" style={primaryBtnStyle}>{busy ? 'Closing…' : 'Close case'}</button>
          <button type="button" onClick={onClose} className="h-11 px-3 text-sm" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
          <span className="ml-auto text-xs" style={{ color: 'var(--ink-faint)' }}>You can reopen it later.</span>
        </div>
      </div>
    </div>
  );
}
